import { diffArrays } from 'diff'

/**
 * HTML-aware inline diff.
 *
 * Given the original (published) HTML and the new (draft) HTML, returns HTML in which
 * added text runs are wrapped in `<ins class="diff-ins">` and removed text runs in
 * `<del class="diff-del">`. Tags are diffed as atomic tokens so markup is never split:
 * added tags are emitted as-is, removed tags are dropped (their text still surfaces in a
 * `<del>` run), and unchanged tags pass through untouched.
 *
 * This is deliberately word/tag granular rather than a full tree diff — it is robust,
 * dependency-light (reuses jsdiff, already bundled), and good enough to show a reviewer
 * exactly which words changed in the rendered content.
 */

/** Split HTML into a flat token list of whole tags and individual words/whitespace runs. */
function tokenize(html: string): string[] {
  const tokens: string[] = []
  // Each match is either a full tag (`<...>`, incl. comments) or a run of non-tag text.
  const chunkRe = /<!--[\s\S]*?-->|<[^>]+>|[^<]+/g
  let chunk: RegExpExecArray | null
  while ((chunk = chunkRe.exec(html)) !== null) {
    const value = chunk[0]
    if (value[0] === '<') {
      tokens.push(value)
    } else {
      // Split text into alternating whitespace / non-whitespace tokens so word-level
      // changes diff cleanly instead of collapsing whole paragraphs.
      const wordRe = /\s+|[^\s]+/g
      let word: RegExpExecArray | null
      while ((word = wordRe.exec(value)) !== null) tokens.push(word[0])
    }
  }
  return tokens
}

const isTag = (token: string): boolean => token[0] === '<'

/**
 * Produce inline-diff HTML between `oldHtml` (published) and `newHtml` (draft).
 * Returns `newHtml` unchanged when the two are identical.
 */
export function htmlDiff(oldHtml: string, newHtml: string): string {
  if (oldHtml === newHtml) return newHtml

  const parts = diffArrays(tokenize(oldHtml), tokenize(newHtml))

  let out = ''
  let insBuffer = ''
  let delBuffer = ''

  const flush = () => {
    // Removed text renders first (struck through), then added text, mirroring a review diff.
    if (delBuffer) {
      out += `<del class="diff-del">${delBuffer}</del>`
      delBuffer = ''
    }
    if (insBuffer) {
      out += `<ins class="diff-ins">${insBuffer}</ins>`
      insBuffer = ''
    }
  }

  for (const part of parts) {
    if (!part.added && !part.removed) {
      flush()
      out += part.value.join('')
      continue
    }

    for (const token of part.value) {
      if (part.added) {
        // Keep structural tags outside the <ins> wrapper so markup stays valid.
        if (isTag(token)) {
          flush()
          out += token
        } else {
          insBuffer += token
        }
      } else {
        // Removed: drop tags, surface removed text inside a <del> run.
        if (!isTag(token)) delBuffer += token
      }
    }
  }

  flush()
  return out
}
